import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { apiClient, RecipeSummaryItem } from '@/utils/apiClient';

interface RecipeHitRatePanelProps {
  className?: string;
}

export const RecipeHitRatePanel: React.FC<RecipeHitRatePanelProps> = ({ className }) => {
  const { t, language } = useTranslation();
  const counts = t.analytics.counts;
  const at = (t as any).analytics;
  const [data, setData] = useState<RecipeSummaryItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getRecipeSummary(days);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [days, at?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const items = data ?? [];
  const totalHits = items.reduce((sum, r) => sum + r.hit_count, 0);

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700', className)}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
            <BookOpen className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.recipeHitRate ?? 'Recipe Hit Rate'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatCount(language, items.length, counts.recipes)} · {formatCount(language, totalHits, counts.hits)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-4">
          {/* Period selector */}
          <div className="flex gap-2">
            {[7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={clsx(
                  'px-2.5 py-1 text-xs rounded-md transition-colors',
                  days === d
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
              >
                {d === 7 ? (at?.days7 ?? '7 days') : (at?.days30 ?? '30 days')}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">
              {at?.noRecipeHits ?? 'No recipe hits recorded yet'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="pb-2 font-medium">{at?.recipeName ?? 'Recipe'}</th>
                    <th className="pb-2 font-medium text-right">{at?.hitCount ?? 'Hits'}</th>
                    <th className="pb-2 font-medium text-right">{at?.avgDuration ?? 'Avg Duration'}</th>
                    <th className="pb-2 font-medium text-right">{at?.lastFired ?? 'Last Fired'}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const maxHits = items[0]?.hit_count || 1;
                    const pct = (item.hit_count / maxHits) * 100;
                    return (
                      <tr key={item.recipe_name} className="border-b border-gray-50 dark:border-gray-700/50">
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-gray-700 dark:text-gray-300">{item.recipe_name}</span>
                          </div>
                          {/* Hit rate bar */}
                          <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mt-1 max-w-[120px]">
                            <div
                              className="h-full bg-emerald-400 dark:bg-emerald-500 rounded-full"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </td>
                        <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300 font-medium">
                          {item.hit_count}
                        </td>
                        <td className="py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
                          {item.avg_duration_ms != null ? `${item.avg_duration_ms.toFixed(0)} ${at?.ms ?? 'ms'}` : '—'}
                        </td>
                        <td className="py-2 text-right text-gray-500 dark:text-gray-400">
                          {item.last_fired
                            ? new Date(item.last_fired).toLocaleDateString()
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
